import React, { useState } from 'react';
import { Card, List, Space, Typography, Tag, Descriptions, Modal, Divider } from 'antd';
import { Users, Eye } from 'lucide-react';
import { useCrews } from '../hooks/useCrews';
import type { Crew } from '../types';

const { Title, Text } = Typography;

const CrewsPage: React.FC = () => {
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);

  // Use TanStack Query hooks
  const { data: crews = [], isLoading } = useCrews();

  const handleViewDetails = (crew: Crew) => {
    setSelectedCrew(crew);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Users className="inline-block mr-2" size={24} /> Crews
        </Title>
        <Text type="secondary">View crews currently in use by the system</Text>
      </div>

      {crews.length === 0 && !isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Users size={64} className="text-gray-300" />
            <Title level={4} style={{ marginTop: 16 }}>No crews in use</Title>
            <Text type="secondary">There are currently no crews being used by the system</Text>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={crews}
          loading={isLoading}
          renderItem={(crew) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleViewDetails(crew)}
                style={{ cursor: 'pointer' }}
              >
                <Card.Meta
                  title={crew.name}
                  description={crew.description || 'No description'}
                />
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Process: </Text>
                      <Tag color="cyan">{crew.process || 'sequential'}</Tag>
                    </div>
                    <div>
                      <Text type="secondary">Agents: </Text>
                      <Tag color="blue">{crew.agents?.length || 0}</Tag>
                    </div>
                    <div>
                      <Text type="secondary">Tasks: </Text>
                      <Tag color="green">{crew.tasks?.length || 0}</Tag>
                    </div>
                    <Space size="small" wrap>
                      {crew.verbose && <Tag color="orange">Verbose</Tag>}
                      {crew.memory && <Tag color="purple">Memory</Tag>}
                      {crew.cache && <Tag color="geekblue">Cache</Tag>}
                    </Space>
                  </Space>
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Text type="secondary">
                    <Eye size={14} className="inline-block mr-1" /> Click to view details
                  </Text>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title="Crew Details"
        open={!!selectedCrew}
        onCancel={() => setSelectedCrew(null)}
        footer={null}
        width={800}
      >
        {selectedCrew && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Name">{selectedCrew.name}</Descriptions.Item>
              <Descriptions.Item label="Description">
                {selectedCrew.description || 'No description'}
              </Descriptions.Item>
              <Descriptions.Item label="Process">
                <Tag color="cyan">{selectedCrew.process || 'sequential'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Verbose">
                <Tag color={selectedCrew.verbose ? 'orange' : 'default'}>
                  {selectedCrew.verbose ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Memory">
                <Tag color={selectedCrew.memory ? 'purple' : 'default'}>
                  {selectedCrew.memory ? 'Enabled' : 'Disabled'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cache">
                <Tag color={selectedCrew.cache ? 'geekblue' : 'default'}>
                  {selectedCrew.cache ? 'Enabled' : 'Disabled'}
                </Tag>
              </Descriptions.Item>
              {selectedCrew.max_rpm && (
                <Descriptions.Item label="Max RPM">{selectedCrew.max_rpm}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider>Agents ({selectedCrew.agents?.length || 0})</Divider>
            {selectedCrew.agents && selectedCrew.agents.length > 0 ? (
              <List
                dataSource={selectedCrew.agents}
                renderItem={(agent) => (
                  <List.Item>
                    <Card size="small" style={{ width: '100%' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>{agent.name}</Text>
                        <Text type="secondary">{agent.role}</Text>
                        <Text type="secondary" ellipsis>Goal: {agent.goal}</Text>
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">No agents assigned</Text>
            )}

            <Divider>Tasks ({selectedCrew.tasks?.length || 0})</Divider>
            {selectedCrew.tasks && selectedCrew.tasks.length > 0 ? (
              <List
                dataSource={selectedCrew.tasks}
                renderItem={(task) => (
                  <List.Item>
                    <Card size="small" style={{ width: '100%' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>{task.name}</Text>
                        <Text type="secondary" ellipsis>{task.description}</Text>
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">No tasks assigned</Text>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CrewsPage;
